import type { ApiLoopProduct } from '../../common/api/products/apiProductModels';
import type {
    ApiLoopAutocompleteResponse,
    ApiLoopGlobalSearchResponse,
    ApiLoopLoyaltyProduct,
    ApiLoopRecipeSearchResponse,
    ApiLoopSearchArticle,
    ApiLoopSearchRecipeId,
    ApiLoopSearchResponse,
    ApiLoopSearchStore,
} from './responses/searchResponses';
import type {
    ApiAutocompleteApiRequest,
    ApiBasketRecommendationsApiRequest,
    ApiComplementaryRecommendationsApiRequest,
    ApiLoopPopularRecipesRequest,
    ApiLoopSearchRequest,
    ApiPopularProductsApiRequest,
    ApiRecentRecommendationsApiRequest,
    ApiSearchByAttributeApiRequest,
    ApiSearchEntitiesApiRequest,
    ApiSearchGlobalApiRequest,
    ApiSearchProductsApiRequest,
} from './searchRequests';

import { getCancellationToken } from '../../common/api/utils';
import getDirectPersonalizationSearchAxiosClient, {
    prepareRequestParams,
} from './directPersonalizationAxiosClient';

const searchLoyaltyProducts = async ({
    loopSearchRequest,
}: {
    loopSearchRequest: ApiLoopSearchRequest;
}): Promise<ApiLoopSearchResponse<ApiLoopLoyaltyProduct>> => {
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopLoyaltyProduct>>(
        '/search/pointshop',
        loopSearchRequest,
        {
            params: {
                channel: 'Coopse',
            },
        },
    );
    return response.data;
};

const searchRecipes = async ({
    loopSearchRequest,
}: {
    loopSearchRequest: ApiLoopSearchRequest;
}): Promise<ApiLoopRecipeSearchResponse> => {
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopRecipeSearchResponse>(
        '/search/recipes',
        loopSearchRequest,
    );
    return response.data;
};

const searchProducts = async ({
    loopSearchRequest,
    storeId,
    abortSignal,
    searchService,
    customerGroups,
    device,
    quickSearch,
}: ApiSearchProductsApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        '/search/products',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({
                storeId,
                customerGroups,
                device,
                quickSearch,
                searchService,
            }),
        },
    );
    return response.data;
};

const searchArticles = async (
    loopSearchRequest: ApiLoopSearchRequest,
    abortSignal?: AbortSignal,
): Promise<ApiLoopSearchResponse<ApiLoopSearchArticle>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopSearchArticle>>(
        '/search/articles',
        loopSearchRequest,
        { cancelToken: token },
    );
    return response.data;
};
const searchPointshop = async (
    loopSearchRequest: ApiLoopSearchRequest,
    abortSignal?: AbortSignal,
): Promise<ApiLoopSearchResponse<ApiLoopLoyaltyProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopLoyaltyProduct>>(
        '/search/pointshop',
        loopSearchRequest,
        { cancelToken: token },
    );
    return response.data;
};

const getLoyaltyProductsById = async (
    ids: number[],
): Promise<ApiLoopSearchResponse<ApiLoopLoyaltyProduct>> => {
    const client = getDirectPersonalizationSearchAxiosClient(false);
    const response = await client.post<ApiLoopSearchResponse<ApiLoopLoyaltyProduct>>(
        '/search/pointshops/by-id',
        ids,
    );
    return response.data;
};

const searchStores = async (
    loopSearchRequest: ApiLoopSearchRequest,
    abortSignal?: AbortSignal,
): Promise<ApiLoopSearchResponse<ApiLoopSearchStore>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopSearchStore>>(
        '/search/stores',
        loopSearchRequest,
        { cancelToken: token },
    );
    return response.data;
};

const searchGlobal = async ({
    loopGlobalSearchRequest,
    storeId,
    abortSignal,
    customerGroups,
    device,
    quickSearch,
}: ApiSearchGlobalApiRequest): Promise<ApiLoopGlobalSearchResponse> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopGlobalSearchResponse>(
        '/search/global',
        loopGlobalSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch }),
        },
    );
    return response.data;
};

const searchByAttribute = async ({
    loopSearchRequest,
    storeId,
    customerGroups,
    device,
    abortSignal,
}: ApiSearchByAttributeApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        '/search/entities/by-attribute',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch: false }),
        },
    );
    return response.data;
};

const searchEntities = async ({
    loopSearchRequest,
    storeId,
    customerGroups,
    device,
    abortSignal,
}: ApiSearchEntitiesApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        '/search/entities',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch: false }),
        },
    );
    return response.data;
};

const basketRecommendations = async ({
    loopSearchRequest,
    storeId,
    customerGroups,
    device,
    abortSignal,
}: ApiBasketRecommendationsApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        'search/recommend/basket',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch: false }),
        },
    );
    return response.data;
};

const popularProducts = async ({
    loopSearchRequest,
    storeId,
    customerGroups,
    device,
    abortSignal,
}: ApiPopularProductsApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        'search/recommend/popular',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch: false }),
        },
    );
    return response.data;
};

const complementaryProducts = async ({
    loopSearchRequest,
    storeId,
    customerGroups,
    device,
    abortSignal,
}: ApiComplementaryRecommendationsApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        'search/recommend/complementary',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch: false }),
        },
    );
    return response.data;
};

/* get the recent recommendations of what the user looked at / purchased */
const recentRecommendations = async ({
    loopSearchRequest,
    storeId,
    customerGroups,
    device,
    abortSignal,
}: ApiRecentRecommendationsApiRequest): Promise<ApiLoopSearchResponse<ApiLoopProduct>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopSearchResponse<ApiLoopProduct>>(
        'search/recommend/recent',
        loopSearchRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({ storeId, customerGroups, device, quickSearch: true }),
        },
    );
    return response.data;
};

const autoComplete = async ({
    loopAutocompleteRequest,
    abortSignal,
    storeId,
    searchService,
    customerGroups,
    device,
}: ApiAutocompleteApiRequest) => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();
    const response = await client.post<ApiLoopAutocompleteResponse>(
        '/search/auto-complete',
        loopAutocompleteRequest,
        {
            cancelToken: token,
            params: prepareRequestParams({
                storeId,
                customerGroups,
                device,
                quickSearch: false,
                searchService,
            }),
        },
    );
    return response.data;
};

const fetchRecipeRecommendations = async (
    loopSearchRequest: ApiLoopPopularRecipesRequest,
    abortSignal?: AbortSignal,
): Promise<ApiLoopSearchResponse<ApiLoopSearchRecipeId>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopSearchRecipeId>>(
        `search/recommend/recipes/recommendations`,
        loopSearchRequest,
        { cancelToken: token },
    );
    return response.data;
};

const fetchRecipeRecent = async (
    loopSearchRequest: ApiLoopPopularRecipesRequest,
    abortSignal?: AbortSignal,
): Promise<ApiLoopSearchResponse<ApiLoopSearchRecipeId>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopSearchRecipeId>>(
        `search/recommend/recipes/recent`,
        loopSearchRequest,
        { cancelToken: token },
    );
    return response.data;
};

const fetchRecipePopular = async (
    loopSearchRequest: ApiLoopPopularRecipesRequest,
    abortSignal?: AbortSignal,
): Promise<ApiLoopSearchResponse<ApiLoopSearchRecipeId>> => {
    const token = getCancellationToken(abortSignal);
    const client = getDirectPersonalizationSearchAxiosClient();

    const response = await client.post<ApiLoopSearchResponse<ApiLoopSearchRecipeId>>(
        `search/recommend/recipes/popular`,
        loopSearchRequest,
        { cancelToken: token },
    );
    return response.data;
};

export const searchDirectApi = {
    getLoyaltyProductsById,
    searchRecipes,
    searchLoyaltyProducts,
    searchProducts,
    searchArticles,
    searchPointshop,
    searchStores,
    searchGlobal,
    searchByAttribute,
    searchEntities,
    autoComplete,
    basketRecommendations,
    popularProducts,
    complementaryProducts,
    recentRecommendations,
    fetchRecipeRecommendations,
    fetchRecipeRecent,
    fetchRecipePopular,
};
